import { http } from '@/core/api'
import type { LeadZaloStoredMessage } from '@/features/recruitment/api/zalo-dm.api'
import type { ZaloPersonalStatus } from '@/features/settings/api/integrations.api'

export type ZaloHrAccountRow = {
  id: number
  user_id: number
  slot_key: string
  zalo_uid: string | null
  display_name: string | null
  linked_at: string | null
  last_status: string | null
  last_seen_at: string | null
}

export type ZaloHrNickStatus = {
  configured: boolean
  reachable: boolean
  slot: string
  account: ZaloHrAccountRow
  bridge: Partial<ZaloPersonalStatus> & Record<string, unknown>
}

export type ZaloHrInboxItem = {
  id: number
  application_id: number | null
  assignment_id: number | null
  /** Lead was reclaimed/released away from this nick's owner: history stays, sending is blocked. */
  read_only?: boolean
  /** This recruiter's lead was reclaimed. Shown under the chat row. */
  reclaimed?: boolean
  worker_id: number | null
  bridge_own_uid: string
  zalo_peer_uid: string
  display_name: string | null
  /** Zalo profile name — shown beside the lead's real name. */
  zalo_name?: string | null
  avatar: string | null
  zalo_username: string | null
  zalo_gender: number | null
  zalo_cover_url: string | null
  zalo_status_message: string | null
  zalo_birthday: string | null
  preview: string | null
  updated_at: string | null
  /** Owner inbox unread (persisted). */
  unread?: number
  /** Stored Zalo friendship. A thread existing does not mean the peer accepted. */
  is_friend?: boolean
  is_requesting?: boolean
  owner_user_id?: number | null
  owner_name?: string | null
  /** Zalo display name of the nick that owns this thread. */
  bridge_own_name?: string | null
}

export type ZaloHrFriendItem = {
  uid: string
  display_name: string
  avatar: string | null
  alias: string | null
}

export const zaloPersonalAccountApi = {
  /** `cached`: answer from the saved nick when it was confirmed recently (no bridge call). */
  async status(options?: { cached?: boolean }) {
    const { data } = await http.get('/recruitment/zalo-personal/me/status', {
      params: options?.cached ? { cached: 1 } : undefined,
    })
    return data as { data: ZaloHrNickStatus }
  },

  async inbox(options?: {
    conversationId?: number
    limit?: number
    beforeAt?: string | null
    beforeId?: number | null
  }) {
    const params: Record<string, string | number> = {}
    if (options?.conversationId && options.conversationId > 0) {
      params.conversation_id = options.conversationId
    }
    if (options?.limit && options.limit > 0) params.limit = options.limit
    if (options?.beforeAt) params.before_at = options.beforeAt
    if (options?.beforeId && options.beforeId > 0) params.before_id = options.beforeId
    const { data } = await http.get('/recruitment/zalo-personal/me/inbox', {
      params: Object.keys(params).length ? params : undefined,
    })
    return data as {
      data: ZaloHrInboxItem[]
      meta?: { has_more?: boolean; next_before_at?: string | null; next_before_id?: number | null }
    }
  },

  async teamInbox(options?: {
    conversationId?: number
    ownerUserId?: number
    limit?: number
    beforeAt?: string | null
    beforeId?: number | null
  }) {
    const params: Record<string, string | number> = {}
    if (options?.conversationId && options.conversationId > 0) {
      params.conversation_id = options.conversationId
    }
    if (options?.ownerUserId && options.ownerUserId > 0) params.owner_user_id = options.ownerUserId
    if (options?.limit && options.limit > 0) params.limit = options.limit
    if (options?.beforeAt) params.before_at = options.beforeAt
    if (options?.beforeId && options.beforeId > 0) params.before_id = options.beforeId
    const { data } = await http.get('/recruitment/zalo-personal/team/inbox', {
      params: Object.keys(params).length ? params : undefined,
    })
    return data as {
      data: ZaloHrInboxItem[]
      owners?: Array<{ id: number; name: string }>
      meta?: { has_more?: boolean; next_before_at?: string | null; next_before_id?: number | null }
    }
  },

  async teamMessages(
    conversationId: number,
    options?: { afterId?: number; beforeId?: number },
  ) {
    const params: Record<string, number> = {}
    if (options?.afterId && options.afterId > 0) params.after_id = options.afterId
    if (options?.beforeId && options.beforeId > 0) params.before_id = options.beforeId
    const { data } = await http.get(`/recruitment/zalo-personal/team/conversations/${conversationId}/messages`, {
      params: Object.keys(params).length ? params : undefined,
    })
    return data as {
      data: LeadZaloStoredMessage[]
      meta?: { has_more_older?: boolean }
    }
  },

  async friends() {
    const { data } = await http.get('/recruitment/zalo-personal/me/friends')
    return data as { data: ZaloHrFriendItem[] }
  },

  /** Owner-only: clear persisted inbox unread for a conversation. */
  async markConversationRead(conversationId: number) {
    const { data } = await http.post(
      `/recruitment/zalo-personal/me/conversations/${conversationId}/read`,
    )
    return data as { data: { unread: number; cleared: boolean } }
  },

  async startQrLogin() {
    const { data } = await http.post('/recruitment/zalo-personal/me/login/qr')
    return data as { data: ZaloPersonalStatus; message?: string }
  },

  async logout() {
    const { data } = await http.post('/recruitment/zalo-personal/me/logout')
    return data as { data: ZaloPersonalStatus; message?: string }
  },

  async fetchQrImage(): Promise<Blob> {
    const { data } = await http.get<Blob>('/recruitment/zalo-personal/me/login/qr', {
      responseType: 'blob',
    })
    return data
  },

  async inventory(options?: { live?: boolean }) {
    const { data } = await http.get('/recruitment/zalo-personal/accounts', {
      params: options?.live ? { live: 1 } : undefined,
    })
    return data as { data: ZaloAccountsInventory }
  },

  async forceLogout(userId: number) {
    const { data } = await http.post(`/recruitment/zalo-personal/accounts/${userId}/logout`)
    return data as { data: ZaloPersonalStatus; message?: string }
  },

  async forceLogoutShared() {
    const { data } = await http.post('/recruitment/zalo-personal/accounts/shared/logout')
    return data as { data: ZaloPersonalStatus; message?: string }
  },
}

export type ZaloAccountStatusBucket = 'online' | 'offline' | 'reconnecting' | 'unreachable'

export type ZaloAccountInventoryRow = {
  kind: 'hr' | 'shared'
  user_id: number | null
  user_name: string | null
  user_email?: string | null
  employee_status?: string | null
  slot_key: string
  zalo_uid: string | null
  display_name: string | null
  linked_at: string | null
  last_status: string
  last_seen_at: string | null
  online: boolean
  status_bucket: ZaloAccountStatusBucket
}

export type ZaloAccountsInventory = {
  configured: boolean
  summary: {
    online: number
    offline: number
    reconnecting: number
    unreachable: number
    total: number
  }
  accounts: ZaloAccountInventoryRow[]
}
